import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { detailOf } from './failure';

// =====================================================================
// Datadog Browser observability for the dialer-web SPA: RUM (+ Session Replay)
// and browser logs (error forwarding), sharing one session.
//
// AUTH-GATED: collection starts only AFTER the SDR is authenticated. We init the
// SDKs lazily on the authed routes (cockpit / console) — so the /login view is
// never recorded — identify the session by the SDR's email, then start Session
// Replay. On logout / loss of auth we stop replay, end the session, and forget
// the user (stopRum). Sign-out also full-reloads, which tears the SDKs down.
//
// PROD-ONLY, by STRUCTURE not by a trusted flag: `import.meta.env.PROD` is a
// COMPILE-TIME Vite constant that is false under both `vite dev` and vitest
// (MODE=test), and the RUM app id + client token are inlined ONLY by the deploy
// build — so dev/local/test fail TWO independent conditions and never collect.
// (We deliberately avoid a runtime "test mode" env gate; that's brittle — it can
// silently stop matching and record from the wrong place. rum.test.ts asserts
// init() is never called under vitest, so the exclusion is proven, not trusted.)
// We also drop automated/webdriver sessions (E2E, bots) — this is for REAL users.
//
// The app id + client token are PUBLIC client-side values (designed to ship in
// the bundle), provisioned by infrastructure/datadog.ts (datadog.RumApplication)
// and wired into the build by .github/workflows/deploy.yml (VITE_DD_RUM_*),
// dialer-web only.
// =====================================================================

const APP_ID = import.meta.env.VITE_DD_RUM_APPLICATION_ID as string | undefined;
const CLIENT_TOKEN = import.meta.env.VITE_DD_RUM_CLIENT_TOKEN as string | undefined;
const RUM_ENV = (import.meta.env.VITE_DD_RUM_ENV as string | undefined) ?? 'unknown';
const VERSION = import.meta.env.VITE_GIT_SHA as string | undefined;
const SITE = 'datadoghq.eu'; // Datadog EU site (see CLAUDE.md)
const SERVICE = 'dialer-web'; // matches the SVC_DIALER_WEB tag the rest of the stack uses

// Selenium/Playwright/Puppeteer set navigator.webdriver — exclude them so E2E or
// bot traffic against a deployed build never lands in RUM (real users only).
const isAutomated = typeof navigator !== 'undefined' && navigator.webdriver === true;

// Runs only on a real deploy build carrying the app id + token, for a real
// (non-automated) user. PROD is false under `vite dev` and vitest, and the tokens
// are absent there too — so this is false for dev/local/test, SDKs never init.
const ENABLED = Boolean(import.meta.env.PROD && APP_ID && CLIENT_TOKEN && !isAutomated);

let initialised = false;

// Init both SDKs once. Returns false (and does nothing) when disabled.
function ensureInit(): boolean {
  if (!ENABLED) return false;
  if (initialised) return true;

  const base = {
    clientToken: CLIENT_TOKEN as string,
    site: SITE,
    service: SERVICE,
    env: RUM_ENV,
    version: VERSION,
  };

  datadogRum.init({
    ...base,
    applicationId: APP_ID as string,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    // Internal-only tool (operator decision) — record without masking. A lead's
    // email is now typed into the in-call check, so that row masks itself per
    // element; anything else showing a lead's details must do the same, or this
    // moves to 'mask'/'mask-user-input'.
    defaultPrivacyLevel: 'allow',
    // We drive replay start/stop around auth (startRumForUser / stopRum) rather
    // than letting it auto-start on init.
    startSessionReplayRecordingManually: true,
    // Link RUM sessions to the backend APM traces. The BFF + core are OTel-traced
    // and SAME-ORIGIN with the SPA (CloudFront proxies /api/* and /bff/* to them),
    // so inject W3C tracecontext (what OTel reads) into those fetch/XHR calls only.
    allowedTracingUrls: [
      { match: `${window.location.origin}/api`, propagatorTypes: ['tracecontext'] },
      { match: `${window.location.origin}/bff`, propagatorTypes: ['tracecontext'] },
    ],
  });

  // Browser logs: forward uncaught errors + console.error to Datadog, joined to
  // the same RUM session (shared clientToken/service/env). No applicationId —
  // logs are keyed by the client token.
  datadogLogs.init({
    ...base,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
  });

  initialised = true;
  return true;
}

// Start (or resume) collection for an authenticated SDR, identified by email.
// Idempotent: safe to call across authed routes — the SDKs init once and the
// user is (re)set each time. No-op when disabled (dev/local/test/automation).
export function startRumForUser(email: string): void {
  // Swallows its own failures because the callers are route guards: a throw
  // here would reject the load and leave an SDR with no cockpit at all. Losing
  // telemetry is the acceptable half of that trade, and the SDKs are third
  // party — whether init can throw is not ours to promise.
  try {
    if (!ensureInit()) return;
    datadogRum.setUser({ id: email, email });
    datadogLogs.setUser({ id: email, email });
    datadogRum.startSessionReplayRecording();
  } catch {
    // ignore
  }
}

// Stop on logout / loss of auth: stop replay and end the RUM session. We do NOT
// clearUser — that anonymizes the whole session retroactively (the replay would
// show "public user" instead of the SDR). The historical session keeps its email
// attribution (useful for support); a real logout full-reloads, which forgets the
// user for anything after. No-op when never enabled/initialised.
export function stopRum(): void {
  if (!ENABLED || !initialised) return;
  datadogRum.stopSessionReplayRecording();
  datadogRum.stopSession();
}

// Log a WebSocket lifecycle/message event to Datadog browser logs. RUM auto-
// captures fetch/XHR but NOT websocket frames, and the cockpit's realtime flow
// rides the BFF socket — so we log it explicitly. Callers pass a COMPACT context
// (direction, frame kind, key ids — never the full payload or the ?token= JWT) so
// the realtime flow is traceable in Datadog during development. No-op until the
// SDKs are initialised (post-auth, prod) — so dev/local/test never emit.
export function logWs(
  event: string,
  context?: Record<string, unknown>,
  level: 'info' | 'warn' | 'error' = 'info',
): void {
  if (!ENABLED || !initialised) return;
  datadogLogs.logger[level](`ws ${event}`, context);
}

// Record a custom RUM action — a discrete, queryable event that also renders as a
// MARKER ON THE SESSION REPLAY TIMELINE, so a support person watching an SDR's
// recording can see it happen rather than cross-referencing logs by timestamp.
// Keep `context` compact and low-cardinality-ish: these become RUM facets. Bulky
// payloads (full device lists) belong in logAudio instead. No-op until initialised.
export function rumAction(name: string, context?: Record<string, unknown>): void {
  if (!ENABLED || !initialised) return;
  datadogRum.addAction(name, context);
}

// Log an audio/microphone snapshot to Datadog browser logs. Paired with rumAction:
// the action is the compact timeline marker, this carries the FULL device list for
// bulk querying (logs retain longer and take a bigger payload than a RUM action).
//
// NOTE ON DEVICE LABELS: unlike logWs (deliberately payload-free), this sends device
// labels VERBATIM, and they can carry personal names ("Paco's AirPods"). That is the
// point — the label corpus is what lets the built-in-vs-external heuristic be derived
// from real fleet data instead of guessed. Consistent with where this SPA's bar
// already sits (RUM runs defaultPrivacyLevel:'allow' and setUser sends SDR email).
export function logAudio(
  event: string,
  context?: Record<string, unknown>,
  level: 'info' | 'warn' | 'error' = 'info',
): void {
  if (!ENABLED || !initialised) return;
  datadogLogs.logger[level](`audio ${event}`, context);
}

// Log a per-call decision this tab made on its own (cockpit/inbound-guard.ts). Paired
// with a RUM action for the same reason as logAudio: the action is the replay marker,
// this is the queryable/alertable surface. Monitors filter on the stable `event` key,
// never the message title (infrastructure/datadog/monitors/features.ts) — and RUM
// actions are not queryable from a monitor at all, so a decision that only ever became
// an action could not be alerted on.
export function logCall(
  event: string,
  context?: Record<string, unknown>,
  level: 'info' | 'warn' | 'error' = 'info',
): void {
  if (!ENABLED || !initialised) return;
  datadogLogs.logger[level](`call ${event}`, context);
}

// The session id for a person to quote when reporting a problem. Null whenever
// there is none to point at. `getInternalContext()` is a semi-internal read
// whose session id is optional, so this narrows rather than asserts — a shape
// change upstream should lose the handle, not throw inside an error screen.
export function rumSessionId(): string | null {
  if (!ENABLED || !initialised) return null;
  try {
    const id: unknown = datadogRum.getInternalContext()?.session_id;
    return typeof id === 'string' && id !== '' ? id : null;
  } catch {
    return null;
  }
}

// A render failure a boundary caught. `where` names the boundary, so a spike
// attributes to one region rather than to the app.
//
// Takes `unknown` and reads the message through the guarded helper: the router
// types what it hands a boundary as an Error but nothing enforces it, and a
// throw in here would escape the boundary handling the original failure.
//
// Coverage stops where collection does — silent until the SDKs initialise, so a
// tab opened straight onto a page that does not start a session reports
// nothing until it has been on one that does. Closing that means collecting
// from people deliberately excluded so far, which is not a change to make here.
export function reportRenderError(
  error: unknown,
  where: string,
  info?: { componentStack?: string | null },
): void {
  if (!ENABLED || !initialised) return;
  // Third argument, not a context field: only an Error passed there yields the
  // standard error kind, message and stack. In context it arrives as a bare
  // string in a namespace modelled as an object. A non-Error has no stack to
  // keep, so it goes in context where it does not collide.
  const carried = error instanceof Error ? error : undefined;
  datadogLogs.logger.error(
    'render boundary caught',
    {
      event: 'render_boundary_caught',
      where,
      ...(carried ? {} : { thrown: detailOf(error) ?? 'a value that was not an error' }),
    },
    carried,
  );
  datadogRum.addError(error, { where, componentStack: info?.componentStack });
}

// Log a cockpit toast lifecycle event (components/toaster.tsx). Paired with a
// RUM action so each one is also a marker on the session replay.
//
// The condition a toast reports is already logged by whatever raised it, but the
// two are NOT the same population: a dismissed toast stays suppressed while its
// condition keeps firing, so counting conditions overcounts how often an SDR was
// actually shown anything. Only these events measure the toast itself.
export function logToast(
  event: string,
  context?: Record<string, unknown>,
  level: 'info' | 'warn' = 'info',
): void {
  if (!ENABLED || !initialised) return;
  datadogLogs.logger[level](`toast ${event}`, context);
}
